import { PageHeader } from "@safeplate/ui";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  Building2,
  ChevronLeft,
  CreditCard,
  Database,
  KeyRound,
  LifeBuoy,
  Monitor,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import {
  settingsHelp,
  settingsSections,
  type SettingsSection,
} from "../shell/navigation.js";
import { useShell } from "../shell/shell-context.js";

export const sectionIcons: Record<SettingsSection["icon"], LucideIcon> = {
  account: KeyRound,
  business: Building2,
  computer: Monitor,
  data: Database,
  notifications: Bell,
  plan: CreditCard,
  privacy: ShieldCheck,
};

export function SettingsLayout() {
  const shell = useShell();
  const sections = settingsSections(shell.activeRole);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const atIndex = pathname.replace(/\/+$/, "") === "/settings";

  return (
    <div className="lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-10">
      <nav aria-label="Settings" className="hidden lg:block">
        <PageHeader className="mb-5" level={2} title="Settings" />
        <ul className="space-y-1">
          {sections.map((section) => {
            const Icon = sectionIcons[section.icon];
            return (
              <li key={section.key}>
                <Link
                  activeProps={{
                    className: "bg-emerald-800 text-white shadow-sm",
                  }}
                  className="flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-stone-600 transition hover:bg-stone-100 hover:text-stone-950"
                  to={section.to}
                >
                  <Icon
                    aria-hidden="true"
                    className="size-[1.05rem] shrink-0"
                    strokeWidth={2.1}
                  />
                  {section.label}
                </Link>
              </li>
            );
          })}
          <li>
            <a
              className="flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-stone-600 transition hover:bg-stone-100 hover:text-stone-950"
              href={settingsHelp.href}
              rel="noreferrer"
              target="_blank"
            >
              <LifeBuoy
                aria-hidden="true"
                className="size-[1.05rem] shrink-0"
                strokeWidth={2.1}
              />
              <span className="flex-1">{settingsHelp.label}</span>
              <span className="text-xs font-normal text-stone-500">
                {settingsHelp.address}
              </span>
            </a>
          </li>
        </ul>
      </nav>
      <div className="min-w-0">
        {atIndex ? null : (
          <Link
            className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-stone-600 hover:text-stone-950 lg:hidden"
            to="/settings"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
            Settings
          </Link>
        )}
        <Outlet />
      </div>
    </div>
  );
}
